import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  MenuItem,
  Select,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
} from "@mui/material";
import { getAllCOE } from "../../../services/adminServices/CoeServices";
import { getAllCoeLabs } from "../../../services/mentorServices/DailyPracticeServices";
import { useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";

const LandingPage = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [state, setState] = useState({
    selectedCOE: "",
    coes: [],
    labs: [],
    loadingCOEs: true,
    loadingLabs: false,
  });

  const fetchCOEs = useCallback(async () => {
    setState((prevState) => ({ ...prevState, loadingCOEs: true }));
    try {
      const response = await getAllCOE();
      setState((prevState) => ({ ...prevState, coes: response.data.data }));
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to fetch COEs.";
      toast.error(errorMessage);
    } finally {
      setState((prevState) => ({ ...prevState, loadingCOEs: false }));
    }
  }, []);

  const fetchLabs = useCallback(async (coeId) => {
    setState((prevState) => ({ ...prevState, loadingLabs: true, labs: [] }));
    if (!coeId) return;
    try {
      const response = await getAllCoeLabs(coeId);
      setState((prevState) => ({ ...prevState, labs: response.data.data }));
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to fetch labs.";
      toast.error(errorMessage);
    } finally {
      setState((prevState) => ({ ...prevState, loadingLabs: false }));
    }
  }, []);

  useEffect(() => {
    fetchCOEs();
  }, [fetchCOEs]);

  const handleCOEChange = (event) => {
    const coeId = event.target.value;
    setState((prevState) => ({ ...prevState, selectedCOE: coeId }));
    fetchLabs(coeId);
  };

  const handleLabClick = (labId) => {
    const { selectedCOE, labs } = state;
    navigate("/mentor/daily-practice", { state: { coeId: selectedCOE, labId: labId, labs } });
  };

  const { loadingCOEs, coes, loadingLabs, labs, selectedCOE } = state;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: isMobile ? "100%" : 360,
        mx: "auto",
        bgcolor: "background.paper",
        minHeight: isMobile ? "100vh" : "auto",
      }}
    >
      <Typography
        variant={isMobile ? "h6" : "h5"}
        sx={{ fontWeight: "bold", mb: isMobile ? 1 : 2 }}
      >
        Select COE
      </Typography>

      {loadingCOEs ? (
        <CircularProgress size={isMobile ? 30 : 40} />
      ) : (
        <FormControl fullWidth variant="outlined" sx={{ width: isMobile ? "90%" : "100%" }}>
          <InputLabel id="coe-select-label">Select COE</InputLabel>
          <Select
            labelId="coe-select-label"
            value={selectedCOE}
            onChange={handleCOEChange}
            label="Select COE"
            sx={{
              fontSize: isMobile ? "0.9rem" : "1rem",
            }}
          >
            <MenuItem value="">Select COE</MenuItem>
            {coes.map((coe) => (
              <MenuItem key={coe.id} value={coe.id}>
                {coe.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {loadingLabs && (
        <CircularProgress size={isMobile ? 20 : 24} sx={{ alignSelf: "center", mt: 2 }} />
      )}

      {!loadingLabs && labs.length > 0 && (
        <>
          <Typography
            variant={isMobile ? "subtitle2" : "subtitle1"}
            sx={{ mt: isMobile ? 1 : 2 }}
          >
            Available Labs:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? 1.5 : 2,
              width: isMobile ? "90%" : "100%",
            }}
          >
            {labs.map((lab) => (
              <Button
                key={lab.id}
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  py: isMobile ? 1 : 1.5,
                  fontSize: isMobile ? "0.85rem" : "1rem",
                }}
                onClick={() => handleLabClick(lab.id)}
              >
                {lab.lab_name}
              </Button>
            ))}
          </Box>
        </>
      )}

      {!loadingLabs && selectedCOE && labs.length === 0 && (
        <Typography align="center" sx={{ mt: 2, fontSize: isMobile ? "0.9rem" : "1rem" }}>
          No labs found for this COE.
        </Typography>
      )}
    </Box>
  );
};

export default LandingPage;
